import { ref } from 'vue';
import CarRepository from '@/repositories/car/CarRepository.ts';
import {
    Car,
    CarCreateForm,
    CarDecodedComplectation,
    CarSendForModerationForm,
    CarsPaginator,
    CarsRequestFilter,
    CarUpdateForm,
    mapCarCreateFormToFormData,
    mapCarSendForModerationFormToFormData,
    mapCarUpdateFormToFormData,
} from '@/types/car.type.ts';
import { CarLastAuctionListItem } from '@/types/auction.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useCarRepository() {
    const arCars = ref<Car[]>([]);
    const carsPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isCarsLoading, setLoading: setCarsLoading } = useLoading();
    const carRepository = new CarRepository();

    const { getLastAuctionByCarsIds } = useAuctionRepository();

    function getCars(requestFilter: CarsRequestFilter): void {
        setCarsLoading(true);

        carRepository.getCars(requestFilter)
            .then((carsPaginator: CarsPaginator) => {
                const carsIds: number[] = carsPaginator.data.map((car: Car) => car.id);

                if (carsIds.length === 0) {
                    arCars.value = carsPaginator.data;
                    carsPaginationMeta.value = carsPaginator.meta;
                    setCarsLoading(false);

                    return;
                }

                getLastAuctionByCarsIds(carsIds).then((auctions: CarLastAuctionListItem[]) => {
                    const auctionsByCarsIds: Record<number, CarLastAuctionListItem> = {};
                    auctions.forEach((auction: CarLastAuctionListItem) => {
                        auctionsByCarsIds[auction.car_id] = auction;
                    });

                    carsPaginator.data.forEach((car: Car) => {
                        car.lastAuction = auctionsByCarsIds[car.id] ?? undefined;
                    });

                    arCars.value = carsPaginator.data;
                    carsPaginationMeta.value = carsPaginator.meta;

                    setCarsLoading(false);
                });
            });
    }

    function approveCar(id: number): Promise<void> {
        return carRepository.approveCar(id);
    }

    function rejectCar(id: number, comment: string): Promise<void> {
        return carRepository.rejectCar(id, comment);
    }

    function createCar(createForm: CarCreateForm): Promise<number> {
        return carRepository.createCar(mapCarCreateFormToFormData(createForm));
    }

    function getComplectationByVin(vin: string): Promise<CarDecodedComplectation> {
        return carRepository.decodeVin(vin);
    }

    function getCar(id: number): Promise<Car> {
        return new Promise<Car>((resolve, reject) => {
            carRepository.getCar(id)
                .then((car: Car) => {
                    getLastAuctionByCarsIds([car.id])
                        .then((auctions: CarLastAuctionListItem[]) => {
                            car.lastAuction = auctions[0] ?? undefined;

                            resolve(car);
                        })
                        .catch(() => resolve(car));
                })
                .catch(() => reject());
        });
    }

    function updateCar(carId: number, updateForm: CarUpdateForm): Promise<void> {
        return carRepository.updateCar(carId, mapCarUpdateFormToFormData(updateForm));
    }

    function sendToModerationCar(carId: number, sendForModerationForm: CarSendForModerationForm): Promise<void> {
        return carRepository.sendToModerationCar(carId, mapCarSendForModerationFormToFormData(sendForModerationForm));
    }

    function deleteCar(carId: number): Promise<void>
    {
        return carRepository.deleteCar(carId);
    }

    function recreateCar(carId: number): Promise<number>
    {
        return carRepository.recreateCar(carId);
    }

    return {
        arCars,
        carsPaginationMeta,
        isCarsLoading,
        getCars: getCars,
        approveCar: approveCar,
        rejectCar: rejectCar,
        createCar: createCar,
        getComplectationByVin: getComplectationByVin,
        getCar: getCar,
        updateCar: updateCar,
        sendToModerationCar: sendToModerationCar,
        deleteCar: deleteCar,
        recreateCar: recreateCar,
    };
}
